import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatBot() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm Krieger AI. I can answer questions about our 90% commission structure, locations, technology tools, and the benefits of joining Krieger Real Estate. How can I help you today?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: text, session_id: sessionId },
        { withCredentials: true }
      );
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (err) {
      const errorMsg = err.response?.status === 401
        ? "Please log in to use the AI assistant."
        : "Sorry, I'm having trouble connecting. Please try again.";
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat bubble */}
      <button
        onClick={() => setOpen(!open)}
        className="chat-bubble w-14 h-14 bg-[#002FA7] hover:bg-[#0040D6] text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-105"
        data-testid="chat-toggle-btn"
        style={{ boxShadow: '0 4px 20px rgba(0, 47, 167, 0.3)' }}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Chat window */}
      {open && (
        <div className="chat-window flex flex-col" data-testid="chat-window">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 bg-white">
            <div className="w-8 h-8 bg-[#002FA7] rounded-lg flex items-center justify-center">
              <Bot size={16} className="text-white" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-800 font-['Cabinet_Grotesk']">Krieger AI</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest">Real Estate Assistant</div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px] bg-[#FAFBFC]" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-[#002FA7] text-white'
                      : 'bg-white text-slate-700 border border-slate-100 shadow-sm'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white text-slate-400 px-3 py-2 text-sm rounded-lg border border-slate-100">
                  <span className="animate-pulse">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-100 p-3 flex gap-2 bg-white">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder={user ? "Ask about Krieger Real Estate..." : "Log in to chat..."}
              disabled={!user}
              className="flex-1 bg-[#F4F6F8] border border-slate-200 text-slate-800 text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#002FA7] placeholder:text-slate-400"
              data-testid="chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading || !user}
              className="bg-[#002FA7] hover:bg-[#0040D6] disabled:opacity-40 text-white px-3 py-2 rounded-lg transition-colors"
              data-testid="chat-send-btn"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
